import {
  defaultSerializeQueryArgs,
  type SerializeQueryArgs,
} from '@reduxjs/toolkit/query'
import { isEqual, omit } from 'lodash-es'
import { z } from 'zod/v4'
import type { listResourceSchema } from '@/types/schema/api/list-resource-schema'

type NamedPaginatedResponse<DataKey extends string> = {
  data: z.infer<typeof listResourceSchema> & {
    [key in DataKey]: unknown[]
  }
}

const getNamedInfiniteQueryConfig = <
  RequestType,
  ResultType extends NamedPaginatedResponse<DataKey>,
  DataKey extends string,
>(
  dataKey: DataKey,
): {
  serializeQueryArgs: SerializeQueryArgs<RequestType>
  merge: (currentCache: ResultType, newResponse: ResultType) => void
  forceRefetch: (params: {
    currentArg: RequestType | undefined
    previousArg: RequestType | undefined
  }) => boolean
} => {
  return {
    serializeQueryArgs: ({
      endpointName,
      queryArgs,
      endpointDefinition,
    }): string => {
      const cacheKey = defaultSerializeQueryArgs({
        endpointName,
        queryArgs: queryArgs ? omit(queryArgs, ['page']) : undefined,
        endpointDefinition,
      })
      return cacheKey //infinite setting need to use the same endpointName
    },
    merge: (currentCache, newResponse): void => {
      const needReset = newResponse.data.page === 1
      const updateCache = {
        data: {
          ...newResponse.data,
          [dataKey]: needReset
            ? newResponse.data[dataKey]
            : [...currentCache.data[dataKey], ...newResponse.data[dataKey]],
        },
      }
      Object.assign(currentCache, updateCache)
    },
    // Refetch when the page arg changes
    forceRefetch({ currentArg, previousArg }): boolean {
      return !isEqual(currentArg, previousArg)
    },
  }
}

export default getNamedInfiniteQueryConfig
